import exportFromJSON from 'export-from-json';

/**
 * Client-side export of an inventory editor's field grid (the merged
 * table → field → descriptor structure every inventory screen edits) as a flat
 * per-field row list, downloadable as CSV or JSON via the page-header Actions
 * dropdown. Export reflects what's on screen, including unsaved edits.
 */

/** "SSN:12; EMAIL:3" from the findings shapes the app tolerates (object, array, scalar). */
function findingsSummary(findings) {
  if (findings == null) return '';
  if (typeof findings === 'object' && !Array.isArray(findings)) {
    return Object.entries(findings)
      .map(([code, v]) => (Number(v) ? `${code}:${Number(v)}` : String(code)))
      .join('; ');
  }
  if (Array.isArray(findings)) {
    return findings
      .map((f) => {
        if (f == null) return null;
        if (typeof f === 'object' && !Array.isArray(f)) {
          const k = Object.keys(f)[0];
          return k != null ? String(k) : null;
        }
        return String(f);
      })
      .filter(Boolean)
      .join('; ');
  }
  return String(findings);
}

/** Flatten table → field → descriptor into stable, spreadsheet-friendly rows. */
export function inventoryFieldsToExportRows(selectedFields) {
  const rows = [];
  for (const table of Object.keys(selectedFields || {}).sort()) {
    const fields = selectedFields[table] || {};
    for (const field of Object.keys(fields).sort()) {
      const fd = fields[field] || {};
      rows.push({
        table,
        field,
        dataType: fd.dataType || '',
        selected: Boolean(fd.checked),
        algorithm: fd.algorithm || '',
        findings: findingsSummary(fd.findings),
      });
    }
  }
  return rows;
}

/**
 * Download the field grid as `<baseName>-<stamp>.<csv|json>`. Returns the row
 * count so hosts can skip the notification when there was nothing to export.
 */
export function exportInventoryFields(selectedFields, format, baseName) {
  const rows = inventoryFieldsToExportRows(selectedFields);
  if (rows.length === 0) return 0;
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const slug =
    String(baseName || 'inventory')
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'inventory';
  exportFromJSON({
    data: rows,
    fileName: `${slug}-${stamp}`,
    exportType: format === 'csv' ? exportFromJSON.types.csv : exportFromJSON.types.json,
  });
  return rows.length;
}
